import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, QueryList, Renderer2, signal, ViewChild, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { VideoComponent } from '../../../common/components';
import { HotspotContentComponent } from './hotspot-content/hotspot-content.component';
import { AutoplayConfig, BundleType, HotspotLayoutEnum, HotspotVideoBehaviourEnum, MediaTypeOption, QuestionType, SlideHotspotsClickspotsDTO } from '../../../common/models';
import { SlideHotspotsDTO } from '../../../common/models/slide-hotspots.model';
import { PaddingPosition } from '../../../common/directives';
import { HotspotService } from '../../../common/services';
import { HotspotMarkerComponent } from './hotspot-marker/hotspot-marker.component';
import { CdkDragEnd } from '@angular/cdk/drag-drop';

@Component({
    selector: 'lib-hotspots-widget',
    templateUrl: './hotspots-widget.component.html',
    styleUrls: ['./hotspots-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HotspotsWidgetComponent implements OnInit, OnDestroy {

  @ViewChild('backgroundVideo') backgroundVideoComponent?: VideoComponent;
  @ViewChild('hotspotsContainer') hotspotsContainer!: ElementRef<HTMLElement>;
  @ViewChildren(HotspotMarkerComponent) hotspotMarkerComponentList?: QueryList<HotspotMarkerComponent>;
  @ViewChildren(HotspotContentComponent) hotspotContentComponentList?: QueryList<HotspotContentComponent>;

  @Input() hotspots!: SlideHotspotsDTO;
  @Input() id?: string;
  @Input() bundle?: any;
  @Input() layout?: HotspotLayoutEnum;
  @Input() langcode?: string;
  @Input() isTranslation?: boolean;
  @Input() spacing?: PaddingPosition;
  @Input() showEditButton = false;
  @Input() showQuestionMenu = false;
  @Input() showCorrectAnswer?: string;
  @Input() allowDrag = false;
  @Input() disableCorrectSelection = false;
  @Input() resizable = false;
  @Input() selectedHash?: string;
  @Input() autoplay = true;

  @Output() interactionCompleted: EventEmitter<number> = new EventEmitter();
  @Output() containerClick: EventEmitter<MouseEvent> = new EventEmitter();
  @Output() spotClick: EventEmitter<number> = new EventEmitter();
  @Output() edit: EventEmitter<number> = new EventEmitter();
  @Output() toggleCorrect: EventEmitter<number> = new EventEmitter();
  @Output() remove: EventEmitter<number> = new EventEmitter();
  @Output() backgroundLoaded: EventEmitter<void> = new EventEmitter();
  @Output() sizeChanged: EventEmitter<{ width: number, height: number, index: number}> = new EventEmitter();
  @Output() updateSpotPosition: EventEmitter<{ event: CdkDragEnd, container: HTMLElement, index: number }> = new EventEmitter();

  public backgroundMediaLoaded = signal(false);

  public showMarkers = false;
  public mediaTypes: typeof MediaTypeOption = MediaTypeOption;
  public activeClickSpotIndex = -1;
  public autoplayConfig: AutoplayConfig = {
    enabled: true,
    volume: 1,
    muted: true
  };

  public readonly types: any = { ...BundleType, ...QuestionType };
  public readonly hotspotVideoBehaviour: typeof HotspotVideoBehaviourEnum = HotspotVideoBehaviourEnum;

  private completedClickspots: boolean[] = [];
  private readonly today = new Date().setHours(0, 0, 0, 0);

  private readonly mediaQuery = window.matchMedia('(min-width: 992px)');
  private readonly unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private readonly renderer: Renderer2,
    private readonly elementRef: ElementRef,
    private readonly hotspotService: HotspotService
  ) { }

  ngOnInit() {
    this.closeAllContent();
    this.completedClickspots = new Array(this.hotspots?.clickspots?.length).fill(false);

    this.hotspotService.onOpen.pipe(
      takeUntil(this.unsubscribe),
    ).subscribe((id: string) => {
      if (!this.hotspots.clickspots?.length) {
        return;
      }

      const index = this.hotspots.clickspots.findIndex(item => item.id === id) || -1;
      if (index === -1 || this.hotspots.clickspots.length <= index) {
        return;
      }

      this.toggleModal(index);
    });
  }

  @HostListener('window:click') public onClick() {
    if (this.mediaQuery.matches) {
      return;
    }

    this.closeAllContent();
  }

  public closeAllContent(): void {
    this.hotspotContentComponentList?.forEach(item => item.close());
    this.activeClickSpotIndex = -1;
  }

  public playHotspotVideo(event: Event, video: VideoComponent): void {
    event.stopPropagation();

    video?.plyr?.playing ? video?.pauseVideo() : video?.startVideo();
  }

  public toggleModal(i: number): void {
    this.spotClick.emit(i);

    if (this.bundle === QuestionType.QUESTION_HOTSPOTS) {
      return;
    }

    const component = this.hotspotContentComponentList?.get(i);
    if (this.activeClickSpotIndex === i) {
      component?.close();
      this.activeClickSpotIndex = -1;
      if (this.hotspotContentComponentList?.toArray()?.every(item => !item.isOpen)) {
        this.removeParentZIndex();
      }

      return;
    }

    this.closeAllContent();
    setTimeout(() => {
      const button = this.hotspotMarkerComponentList?.get(i)?.element.nativeElement;
      component?.open(button);
      this.activeClickSpotIndex = i;
      this.completedClickspots[i] = true;
      this.setParentZIndex();
      this.checkAllClickspotsCompleted();
    })
  }

  public onBackgroundLoad(): void {
    const videoLoaded = this.hotspots.videoBehaviour === HotspotVideoBehaviourEnum.LOOP && !!this.hotspots.background?.mediaVideo;
    this.showMarkers = videoLoaded || !!this.hotspots.background?.mediaImage;
    this.backgroundLoaded.emit();
    this.backgroundMediaLoaded.set(true);
  }

  public onVideoEnded(): void {
    this.showMarkers = true;
  }

  public replayVideo(): void {
    this.backgroundVideoComponent?.plyr?.restart();
    this.backgroundVideoComponent?.startVideo()
    this.showMarkers = false;
  }

  public toggleVideoMute(): void {
    if (!this.backgroundVideoComponent){
      return;
    }

    this.backgroundVideoComponent.plyr.muted = !this.backgroundVideoComponent.plyr.muted
    if (!this.backgroundVideoComponent.plyr.muted) {
      this.backgroundVideoComponent.plyr.volume = 1;
    }
  }

  public onCdkDragEnded(event: CdkDragEnd, index: number): void {
    this.updateSpotPosition.emit({ event, container: this.hotspotsContainer?.nativeElement, index });
  }

  public onCdkDragStarted(): void {
    this.closeAllContent();
  }

  public onContainerMousedown(event: MouseEvent): void {
    const spotOpen = this.hotspotContentComponentList?.toArray()?.some(item => item.isOpen);

    if (spotOpen) {
      this.closeAllContent();
      return;
    }

    this.containerClick.emit(event);
  }

  public trackByFn(_index: number, item: SlideHotspotsClickspotsDTO): string {
    return item.id;
  }

  private checkAllClickspotsCompleted(): void {
    const completedLength = this.completedClickspots.filter(item => item === true).length;

    this.interactionCompleted.emit(completedLength / (this.hotspots.clickspots?.length || 1));
  }

  private setParentZIndex(): void {
    const sectionElement = this.elementRef.nativeElement.parentElement?.parentElement;
    const zIndex = Math.floor((Date.now() - this.today) / 100);
    this.renderer.setStyle(sectionElement, 'z-index', zIndex);
  }

  private removeParentZIndex(): void {
    const sectionElement = this.elementRef.nativeElement.parentElement?.parentElement;
    this.renderer.removeStyle(sectionElement, 'z-index');
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
